// TestimonialsCarousel — compact horizontal slider used in the right
// column of each directions card. Designed to fit the available space
// below the property intro while staying visually balanced with the map
// and image on the left.
//
// One testimonial visible at a time, smooth cross-fade between slides,
// dot pagination + prev/next chevrons that match the room-gallery
// pattern used elsewhere on the site.

import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const TestimonialsCarousel = ({
  testimonials = [],
  lang = "en",
  autoplay = true,
  intervalMs = 7000,
  testIdPrefix = "testimonials",
}) => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const hoverRef = useRef(false);

  const safe = (i) =>
    !testimonials.length ? 0 : ((i % testimonials.length) + testimonials.length) % testimonials.length;

  useEffect(() => {
    if (!autoplay || testimonials.length < 2) return undefined;
    timerRef.current = window.setInterval(() => {
      if (!hoverRef.current) setIdx((cur) => safe(cur + 1));
    }, intervalMs);
    return () => window.clearInterval(timerRef.current);
  }, [autoplay, intervalMs, testimonials.length]);

  if (!testimonials.length) return null;

  const goPrev = () => setIdx((cur) => safe(cur - 1));
  const goNext = () => setIdx((cur) => safe(cur + 1));
  const active = safe(idx);
  const hasMultiple = testimonials.length > 1;

  return (
    <section
      className="relative bg-[#FBF6EE]/70 border border-[#E8DFCF] px-5 sm:px-6 py-6"
      onMouseEnter={() => (hoverRef.current = true)}
      onMouseLeave={() => (hoverRef.current = false)}
      aria-roledescription="carousel"
      aria-label="Guest testimonials"
      data-testid={testIdPrefix}
    >
      {/* Subtle opening quote mark */}
      <Quote
        className="w-7 h-7 text-[#C16542]/35 shrink-0"
        strokeWidth={1.4}
        aria-hidden="true"
      />

      {/* Stacked cross-faded testimonials, anchored to a min-height to
          prevent layout shift between quotes of different lengths. */}
      <div className="relative mt-3 min-h-[140px]">
        {testimonials.map((tm, i) => {
          const isActive = i === active;
          const place = tm.place || pick(tm.location, lang) || "";
          const travelType = pick(tm.travel_type, lang) || "";
          return (
            <article
              key={`tm-${i}`}
              className={`absolute inset-0 transition-opacity duration-700 ease-out ${
                isActive ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              aria-hidden={!isActive}
              data-testid={`${testIdPrefix}-slide-${i}`}
            >
              <p className="font-serif-x italic text-lg leading-[1.45] text-[#2C2621]">
                {pick(tm.text, lang)}
              </p>
              <footer className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] tracking-[0.22em] uppercase">
                <span className="text-[#C16542]" data-testid={`${testIdPrefix}-name-${i}`}>
                  {tm.name}
                </span>
                {place ? (
                  <span className="text-[#5C5248]">· {place}</span>
                ) : null}
                {travelType ? (
                  <span className="text-[#5C5248]">· {travelType}</span>
                ) : null}
              </footer>
            </article>
          );
        })}
      </div>

      {hasMultiple ? (
        <div className="mt-4 flex items-center justify-between gap-3">
          {/* Dot pagination */}
          <div className="flex items-center gap-2" role="tablist">
            {testimonials.map((_, i) => {
              const isOn = i === active;
              return (
                <button
                  type="button"
                  key={`dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`transition-all duration-300 rounded-full ${
                    isOn ? "bg-[#C16542] w-6 h-1.5" : "bg-[#2C2621]/25 hover:bg-[#2C2621]/40 w-1.5 h-1.5"
                  }`}
                  aria-label={`Show testimonial ${i + 1}`}
                  aria-selected={isOn}
                  role="tab"
                  data-testid={`${testIdPrefix}-dot-${i}`}
                />
              );
            })}
          </div>
          {/* Prev / next */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={goPrev}
              className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:text-[#C16542] hover:border-[#C16542] transition-colors rounded-full"
              aria-label="Previous testimonial"
              data-testid={`${testIdPrefix}-prev`}
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:text-[#C16542] hover:border-[#C16542] transition-colors rounded-full"
              aria-label="Next testimonial"
              data-testid={`${testIdPrefix}-next`}
            >
              <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TestimonialsCarousel;
